<script lang="ts">
  // App-wide toast stack. Mounted ONCE in routes/+layout.svelte. Subscribes to
  // the `toasts` store (lib/notifications.ts) and renders each as a dismissible
  // card in a fixed bottom-right column.
  //
  // Accessibility: error toasts are `role="alert"` + `aria-live="assertive"`
  // (interrupt — a failed action the user needs to know about now); success /
  // info are `role="status"` + `aria-live="polite"`. Each has a dismiss button.
  // Entrance/exit animation is suppressed under `prefers-reduced-motion`.

  import { fly } from 'svelte/transition';
  import { toasts, dismiss, type ToastSeverity } from '$lib/stores/notifications';
  import { X } from '$lib/ui/icons';
  import { t } from '$lib/i18n';
  import { UiKeys } from '@voicedojo/backend-types';

  const reducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ariaLive(severity: ToastSeverity): 'assertive' | 'polite' {
    return severity === 'error' ? 'assertive' : 'polite';
  }

  function role(severity: ToastSeverity): 'alert' | 'status' {
    return severity === 'error' ? 'alert' : 'status';
  }

  // 0 duration disables the transition under reduced-motion while keeping the
  // `transition:` directive (and its DOM contract) in place.
  const flyParams = $derived(reducedMotion ? { duration: 0 } : { y: 16, duration: 200 });
</script>

<div class="toaster no-print" aria-label={t(UiKeys.Toast.RegionAria)}>
  {#each $toasts as toast (toast.id)}
    <div
      class="toast {toast.severity}"
      role={role(toast.severity)}
      aria-live={ariaLive(toast.severity)}
      transition:fly={flyParams}
    >
      <span class="message">{toast.message}</span>
      <button
        type="button"
        class="dismiss"
        aria-label={t(UiKeys.Toast.DismissAria)}
        onclick={() => dismiss(toast.id)}
      >
        <X size={16} strokeWidth={1.75} aria-hidden="true" focusable="false" />
      </button>
    </div>
  {/each}
</div>

<style>
  .toaster {
    position: fixed;
    bottom: 1rem;
    right: 1rem;
    z-index: 100;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: min(24rem, calc(100vw - 2rem));
    pointer-events: none;
  }

  .toast {
    pointer-events: auto;
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 0.9rem;
    border-radius: 0.5rem;
    box-shadow: 0 4px 12px rgb(0 0 0 / 0.15);
    font-family: var(--font-ui);
    font-size: var(--text-body);
    line-height: 1.35;
    color: var(--on-fill);
  }

  /* Semantic tones (theme-independent) — never the practice accent. */
  .toast.error {
    background: var(--color-recording);
  }

  .toast.success {
    background: var(--color-leaf);
  }

  .toast.info {
    background: var(--color-cobalt);
  }

  .message {
    flex: 1 1 auto;
    word-break: break-word;
  }

  /* `color: inherit` — the toast ground is a solid semantic fill, so the mark
     must stay on `--on-fill` rather than take IconButton's `--ink`. */
  .dismiss {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    background: transparent;
    border: none;
    color: inherit;
    line-height: 1;
    cursor: pointer;
    padding: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    .toast {
      box-shadow: 0 2px 6px rgb(0 0 0 / 0.2);
    }
  }
</style>
